import type { ConsentStrategy } from './ConsentStrategy';

declare global {
  interface Window {
    /** Third-party global — narrowed at runtime, never trusted structurally. */
    UC_UI?: unknown;
  }
}

/** Normalized name the shop must declare in its Usercentrics configuration. */
const UC_SERVICE_NAME = 'birdsview';

/**
 * Integrators type the service label by hand, so `BirdsView`, `Birds View` and `birdsview`
 * must all match. Anything else is a different service and means no consent (fail closed) —
 * there is deliberately no category-level fallback.
 */
function isConsentedBirdsviewService(service: unknown): boolean {
  if (typeof service !== 'object' || service === null) {
    return false;
  }

  const { name, consent } = service as { name?: unknown; consent?: unknown };
  if (typeof name !== 'string' || name.toLowerCase().replace(/\s+/g, '') !== UC_SERVICE_NAME) {
    return false;
  }

  if (typeof consent !== 'object' || consent === null) {
    return false;
  }

  return (consent as { status?: unknown }).status === true;
}

/**
 * Returns the CMP's service-list accessor bound to `UC_UI`, or null while the CMP has not
 * finished loading.
 */
function readServicesAccessor(): (() => unknown) | null {
  const ucUi = window.UC_UI;
  if (typeof ucUi !== 'object' || ucUi === null) {
    return null;
  }

  const accessor = (ucUi as { getServicesBaseInfo?: unknown }).getServicesBaseInfo;

  return typeof accessor === 'function' ? (accessor as () => unknown).bind(ucUi) : null;
}

/**
 * Usercentrics stores consent in localStorage under its own schema, so the only stable public
 * read is the CMP's own API. A throw from `getServicesBaseInfo()` propagates on purpose —
 * `resolveConsent` catches it and treats this strategy as absent for the tick.
 */
export class UsercentricsStrategy implements ConsentStrategy {
  readonly name = 'usercentrics';

  isPresent(): boolean {
    return readServicesAccessor() !== null;
  }

  hasConsent(): boolean {
    const getServices = readServicesAccessor();
    if (getServices === null) {
      return false;
    }

    const services: unknown = getServices();
    if (!Array.isArray(services)) {
      return false;
    }

    return (services as unknown[]).some(isConsentedBirdsviewService);
  }
}
